/**
 * Client runtime: owns the active Market instance and drives it from a rAF loop.
 * Solo: sim time = performance.now(). Multiplayer: sim time derives from the server clock (see room.ts).
 * React subscribes to `useTick` (a version counter bumped at most ~10x/s) and reads the market directly.
 */
import { create } from 'zustand';
import { SIM } from '../../shared/config';
import { kolOfWallet } from '../../shared/kols';
import { Market, type MarketEvent } from '../../shared/market';
import type { Tweet } from '../../shared/types';
import { useSettings } from '../store/settings';
import { load, save } from './storage';

export const useTick = create<{ v: number; fast: number }>(() => ({ v: 0, fast: 0 }));

type Clock = () => number;
interface Rt { market: Market; clock: Clock; mp: boolean; unsub: (() => void) | null }
const listeners = new Set<(e: MarketEvent) => void>();
export function onMarket(fn: (e: MarketEvent) => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }

function soloMarket(): Market {
  const seed = (Date.now() & 0x7fffffff) || 1;
  const m = new Market({ seed, now: performance.now(), latencyMs: useSettings.getState().latencyMs });
  const w = load<{ balance: number } | null>('wallet', null);
  if (w && Number.isFinite(w.balance) && w.balance >= 0) m.balance = Math.round(w.balance);
  // warm the board so it isn't empty on load: replay 90s of history instantly (deterministic)
  const start = performance.now();
  m.time = start - 90_000; (m as unknown as { nextTick: number }).nextTick = Math.ceil(m.time / SIM.TICK_MS) * SIM.TICK_MS;
  m.advance(start);
  return m;
}

const rt: Rt = { market: soloMarket(), clock: () => performance.now(), mp: false, unsub: null };
let solo: Rt | null = null;
function attach() { rt.unsub?.(); rt.unsub = rt.market.on((e) => { for (const l of listeners) l(e); }); }
attach();

export const market = () => rt.market;
export const now = () => rt.clock();
export const isMp = () => rt.mp;

/** Swap in a multiplayer market; restores the solo market on exit. */
export function useMpMarket(m: Market, clock: Clock) { if (!rt.mp) solo = { ...rt }; rt.market = m; rt.clock = clock; rt.mp = true; attach(); bump(); }
export function exitMp() { if (!rt.mp || !solo) return; rt.market = solo.market; rt.clock = () => performance.now(); rt.mp = false; attach(); bump(); }

let lastV = -1, lastBump = 0, lastFast = 0;
function bump() { useTick.setState((s) => ({ v: s.v + 1 })); }
function loop() {
  const t = rt.clock();
  try { rt.market.advance(t); } catch (e) { console.error(e); }
  const pnow = performance.now();
  if (rt.market.version !== lastV && pnow - lastBump > 100) { lastV = rt.market.version; lastBump = pnow; bump(); }
  if (pnow - lastFast > 250) { lastFast = pnow; useTick.setState((s) => ({ fast: s.fast + 1 })); }
  feedTick(pnow);
  requestAnimationFrame(loop);
}
export function startLoop() { requestAnimationFrame(loop); setInterval(() => { if (!rt.mp) save('wallet', { balance: rt.market.balance + rt.market.reserved }); }, 2000); }
// keep running when the tab is hidden (rAF pauses): advance on a timer too
setInterval(() => { if (document.hidden) rt.market.advance(rt.clock()); }, 500);

// ---------------------------------------------------------------- feed
export const useFeed = create<{ tweets: Tweet[]; add: (t: Tweet) => void; clear: () => void }>((set) => ({
  tweets: [],
  add: (t) => set((s) => (s.tweets.some((x) => x.id === t.id) ? s : { tweets: [t, ...s.tweets].slice(0, 120) })),
  clear: () => set({ tweets: [] }),
}));
/** Director posts arrive as scheduled 'tweet' events on the active market; their t is market time. */
function feedTick(_p: number) { /* feed cadence now comes from the market's Narrative Director */ }
onMarket((e) => { if (e.type === 'scenario' && e.roundId === 'director' && e.ev.kind === 'tweet') useFeed.getState().add(e.ev.tweet); });

// Post-fill chatter: a clearly simulated tracker account mentions notable KOL fills AFTER they land.
const TRACKER = { id: 'acct-kol-tracker', handle: 'kol_tracker_sim', name: 'KOL Tracker (SIM)', followers: 61000, verified: true, avatar: 42, sim: false };
let lastChatter = 0;
onMarket((e) => {
  if (e.type !== 'trade' || e.trade.kind !== 'kol' || e.trade.side !== 'buy' || e.trade.lamports < 3e9) return;
  const pn = performance.now(); if (pn - lastChatter < 8000) return; lastChatter = pn;
  const k = kolOfWallet(e.trade.wallet); const tok = rt.market.tokens.get(e.trade.tokenId); if (!k || !tok) return;
  useFeed.getState().add({ id: `kc-${e.trade.id}`, t: rt.market.time, author: TRACKER, kind: 'text', text: `${k.sim} bought $${tok.ticker} (${(e.trade.lamports / 1e9).toFixed(1)} SOL, simulated)` });
});

/** Navigation (hash router). */
export type Route = { view: 'discover' | 'pulse' | 'trackers' | 'portfolio' | 'rankings' | 'train' | 'dev' } | { view: 'token'; id: string } | { view: 'room'; code: string };
function parse(h: string): Route {
  const [, a, b] = h.replace(/^#/, '').split('/');
  if (a === 'token' && b) return { view: 'token', id: b };
  if (a === 'room' && b) return { view: 'room', code: b.toUpperCase() };
  if (a && ['discover', 'pulse', 'trackers', 'portfolio', 'rankings', 'train', 'dev'].includes(a)) return { view: a as 'pulse' };
  return { view: 'pulse' };
}
export const useRoute = create<{ route: Route }>(() => ({ route: parse(location.hash) }));
window.addEventListener('hashchange', () => useRoute.setState({ route: parse(location.hash) }));
export function go(path: string) { if (location.hash !== '#' + path) location.hash = path; }
export function back() { history.length > 1 ? history.back() : go('/pulse'); }
